import { RENDERS } from './renders';

// Core Features — shared data with deep-dive content for the Feature Explorer
// Each feature carries: id, title, short text, icon name, gradient color, full deep-dive panel, a "report", and optional previewImage.

const FEATURE_RENDERS = {
  'multi-level-feed': null,
  'create-studio': RENDERS.storyPortal,
  'messenger': RENDERS.chat,
  'roni': RENDERS.roni,
  'clubs-challenges': null,
  'times-cards': RENDERS.times,
  'profiles': RENDERS.profile,
  'parent-trust': null,
  'physical-recognition': null,
  'international': null,
};

export const coreFeatures = [
  {
    id: 'multi-level-feed',
    title: 'Smart Multi-Level Feed',
    short: 'Interest-based discovery instead of one chaotic endless feed.',
    icon: 'LayoutGrid',
    color: 'from-violet-500 to-purple-700',
    accent: 'violet',
    deepDive: {
      whatItIs: 'ROMIND KIDS uses a structured multi-level feed instead of pushing every child into one endless stream of random content. Children can explore content through categories such as friends, creativity, photos, videos, clubs, challenges, and personal interests. The goal is to reduce meaningless scrolling and help children find content that actually matches what they enjoy.',
      whyKids: 'It makes the app feel more personal, creative, and easier to navigate. A child opens ROMIND KIDS and lands inside spaces that already match what they care about — not a wall of strangers competing for attention.',
      whyParents: 'It creates a more understandable structure than adult-style social platforms built around viral content and maximum engagement. The feed is shaped by interests, not by attention-maximizing algorithms.',
      ecosystem: 'The feed is the entry point into Clubs, Challenges, and Create Studio. It is the layer that turns a child\'s interests into purposeful daily participation rather than passive scrolling.',
    },
    report: {
      purpose: 'Reduce chaotic scrolling and organize content by interests.',
      childBenefit: 'Easier discovery, less overwhelm, more relevant creative content.',
      parentBenefit: 'A more structured experience than adult-style feeds.',
      safetyNote: 'Designed around age-aware categories and safer discovery — not viral mechanics.',
      connectedTo: ['Clubs & Challenges', 'Create Studio', 'Profiles & Identity'],
    },
  },
  {
    id: 'create-studio',
    title: 'Create Studio',
    short: 'A creative space for posts, photos, videos, stories, moods, and projects.',
    icon: 'Palette',
    color: 'from-fuchsia-500 to-pink-700',
    accent: 'fuchsia',
    deepDive: {
      whatItIs: 'Create Studio is the creative heart of ROMIND KIDS. It gives children tools to make posts, photos, videos, stories, mood-based content, creative projects, and expressive updates in a visual environment designed for their age and emotional needs.',
      whyKids: 'The purpose is not only posting — the purpose is self-expression. Children can create something that reflects who they are, what they feel, what they imagine, and what they want to share safely with peers.',
      whyParents: 'A space designed for genuine creative output. Tools are calibrated for ages 9–14, with safer publishing controls and a structured visual environment instead of an open content pipeline.',
      ecosystem: 'Create Studio feeds the Multi-Level Feed, fuels Club activity, and powers participation in creative Challenges. It is where creativity becomes part of the community.',
    },
    report: {
      purpose: 'Give children a structured, expressive creative environment built for their age.',
      childBenefit: 'A real creative toolkit — posts, photos, videos, stories, moods, projects.',
      parentBenefit: 'Creation tools designed for 9–14, not an adult content studio retrofitted for kids.',
      safetyNote: 'Child-safe publishing controls and visual moderation by design.',
      connectedTo: ['Smart Multi-Level Feed', 'Clubs & Challenges', 'Profiles & Identity'],
    },
  },
  {
    id: 'messenger',
    title: 'Child-Safe Messenger',
    short: 'Friend communication with safety logic, reporting, blocking, and RONI guidance.',
    icon: 'MessageCircle',
    color: 'from-cyan-500 to-blue-700',
    accent: 'cyan',
    deepDive: {
      whatItIs: 'Messenger in ROMIND KIDS is designed for safer child-to-child communication. It is not an adult messenger copied into a children\'s app. The messenger is built around friend-based interaction, safer communication principles, reporting, blocking, and RONI support.',
      whyKids: 'Children need a place to talk to friends — but they also need boundaries and help when communication becomes confusing, uncomfortable, or unsafe. Messenger is designed to support friendship while keeping safety visible and accessible.',
      whyParents: 'Communication is structured around the friends system, with reporting, blocking, and RONI guidance built directly into the conversation. Unsolicited contact from unknown users is limited by design.',
      ecosystem: 'Messenger works alongside RONI, the friends graph, and the platform-level safety architecture. It is communication with safety as a continuous layer, not a separate setting.',
    },
    report: {
      purpose: 'Enable child-safe friend communication, not adult-style open messaging.',
      childBenefit: 'A friendly, age-appropriate place to talk with real friends.',
      parentBenefit: 'Friend-based system + reporting + blocking + RONI guidance built in.',
      safetyNote: 'Designed to limit unsolicited contact and surface safety tools in-conversation.',
      connectedTo: ['RONI Safety Companion', 'Profiles & Identity', 'Parent-Trust Safety Architecture'],
    },
  },
  {
    id: 'roni',
    title: 'RONI Safety Companion',
    short: 'AI safety support and friendly guidance for children.',
    icon: 'Sparkles',
    color: 'from-emerald-500 to-cyan-700',
    accent: 'emerald',
    deepDive: {
      whatItIs: 'RONI is an AI safety companion created for the ROMIND KIDS environment. RONI helps guide children away from harmful wording, risky interactions, and unsafe communication patterns. But RONI is not only a safety tool.',
      whyKids: 'RONI is also designed as a friendly companion and emotional support — especially when a child is new to the platform, has not yet found friends, or needs encouragement. RONI should feel warm, helpful, and protective, never cold, controlling, or scary.',
      whyParents: 'RONI is a continuous in-app safety layer. It surfaces unsafe patterns to the child in the moment, supports kinder phrasing, and connects directly to reporting and blocking. It is companion-first, surveillance-never.',
      ecosystem: 'RONI is woven into Messenger, Create Studio, and the Feed. It is the safety presence that exists wherever children speak, create, or meet new peers.',
    },
    report: {
      purpose: 'Provide in-context safety guidance and a warm AI companion for children.',
      childBenefit: 'A friendly helper that supports kind communication and creative confidence.',
      parentBenefit: 'AI-assisted safety integrated into communication — not a surveillance dashboard.',
      safetyNote: 'Designed as guidance, not monitoring. No AI guarantees absolute safety.',
      connectedTo: ['Child-Safe Messenger', 'Clubs & Challenges', 'Parent-Trust Safety Architecture'],
    },
  },
  {
    id: 'clubs-challenges',
    title: 'Clubs & Challenges',
    short: 'Interest-based communities and creative challenges designed around children\'s real needs.',
    icon: 'Users',
    color: 'from-indigo-500 to-violet-700',
    accent: 'indigo',
    deepDive: {
      whatItIs: 'Clubs and Challenges are the living heart of ROMIND KIDS. They turn the platform from passive scrolling into purposeful creativity, friendship, discovery, and participation.',
      whyKids: 'Clubs help children find peers who share their interests: drawing, music, reading, fantasy, games, science, culture, languages, friendship, crafts, storytelling, photo/video creativity, and more. Challenges are age-aware creative activities that motivate creation and confidence.',
      whyParents: 'Clubs are small, interest-based, moderated spaces — not viral arenas. Challenges are not popularity contests and not random engagement traps. The model favors belonging over follower counts.',
      ecosystem: 'Clubs and Challenges connect to Create Studio (where the work happens), the Feed (where it surfaces), and Profiles (where a child\'s creative identity grows). They are the social and creative engine of the app.',
    },
    report: {
      purpose: 'Replace passive scrolling with purposeful, age-aware creative participation.',
      childBenefit: 'A real reason to create — peers, themes, and small creative goals.',
      parentBenefit: 'Structured communities with age-aware tone and moderation, not viral arenas.',
      safetyNote: 'Tone, depth, and themes shift between 9–11 and 12–14 cohorts.',
      connectedTo: ['Create Studio', 'Smart Multi-Level Feed', 'International Friendship & Language Exchange'],
    },
  },
  {
    id: 'times-cards',
    title: 'Times Cards',
    short: 'A controlled time-based gifting and collection mechanic.',
    icon: 'Clock',
    color: 'from-orange-500 to-amber-700',
    accent: 'orange',
    deepDive: {
      whatItIs: 'Times Cards are a unique ROMIND KIDS mechanic connected to time, friendship, collection, and positive participation. Children may be able to collect and send limited time-based gifts to friends under controlled rules.',
      whyKids: 'It is fun, meaningful, and friendship-positive — a way to give something small to a friend, marked by time and intention rather than by money or status.',
      whyParents: 'The system is parent-aware and structured. It is not gambling, not paid chance, not a random prize mechanic, and not designed to extract money from families. Final rules and any required parental consent will be communicated transparently.',
      ecosystem: 'Times Cards link friends, the Feed, and Profiles. They are a positive social mechanic — not an addictive loop.',
    },
    report: {
      purpose: 'Offer a positive, time-based friendship-gift mechanic without gambling logic.',
      childBenefit: 'A small, meaningful way to share with friends.',
      parentBenefit: 'No real-money chance mechanics. No randomized prize systems.',
      safetyNote: 'Time-limited and rule-controlled by design. Not a lottery.',
      connectedTo: ['Profiles & Identity', 'Physical Recognition Awards', 'Child-Safe Messenger'],
    },
  },
  {
    id: 'profiles',
    title: 'Profiles & Identity',
    short: 'Child-safe profiles with avatars, auras, frames, interests, and creative identity.',
    icon: 'User',
    color: 'from-rose-500 to-pink-700',
    accent: 'rose',
    deepDive: {
      whatItIs: 'Profiles in ROMIND KIDS are designed to help children express identity safely. Instead of exposing private data, profiles focus on creative and age-appropriate elements such as avatars, auras, frames, interests, clubs, achievements, and visual style.',
      whyKids: 'A child needs to feel that their profile belongs to them — that it represents who they are. ROMIND KIDS profiles are expressive without forcing children to expose personal details.',
      whyParents: 'Profiles do not expose unnecessary sensitive information such as precise location, school, address, or unsafe personal details. The default is privacy-conscious public identity.',
      ecosystem: 'Profiles connect Clubs, Challenges, the Feed, and friend relationships. They are the visual centre of a child\'s creative life on the platform.',
    },
    report: {
      purpose: 'Allow expressive, creative identity without sensitive personal data.',
      childBenefit: 'Avatars, auras, frames, interests — identity as creativity.',
      parentBenefit: 'No precise location, school, or address exposed by default.',
      safetyNote: 'Privacy-conscious public identity is the default state.',
      connectedTo: ['Create Studio', 'Clubs & Challenges', 'Parent-Trust Safety Architecture'],
    },
  },
  {
    id: 'parent-trust',
    title: 'Parent-Trust Safety Architecture',
    short: 'Safety structure designed so parents can understand and trust the environment.',
    icon: 'ShieldCheck',
    color: 'from-purple-500 to-fuchsia-700',
    accent: 'purple',
    deepDive: {
      whatItIs: 'The creative design of ROMIND KIDS is built for children. The safety architecture is built so parents can trust it. This includes parent-aware onboarding, age-aware experience logic, privacy-conscious profiles, no precise location exposure, safer messaging principles, reporting and blocking, RONI guidance, and future parent control logic.',
      whyKids: 'Children get a real, child-centered universe — not a parental control app dressed up as a platform. Safety is built into the experience, so it never feels restrictive or surveilled.',
      whyParents: 'ROMIND KIDS gives parents a coherent safety architecture to evaluate before saying "yes" — not a dashboard to police their child. The trust contract is built into the product itself.',
      ecosystem: 'This architecture wraps every other feature — Messenger, RONI, Profiles, Feed, Clubs, Create Studio, Times Cards — and ties them together into a single safety story.',
    },
    report: {
      purpose: 'Make trust possible at the architecture level — not as a feature add-on.',
      childBenefit: 'A child-centered universe protected from the inside.',
      parentBenefit: 'A coherent safety logic across every feature of the platform.',
      safetyNote: 'No claim of absolute safety. A continuous design layer, not a single switch.',
      connectedTo: ['RONI Safety Companion', 'Profiles & Identity', 'Child-Safe Messenger'],
    },
  },
  {
    id: 'physical-recognition',
    title: 'Physical Recognition Awards',
    short: 'A planned long-term recognition path for creativity, kindness, and positive participation.',
    icon: 'Award',
    color: 'from-amber-400 to-yellow-700',
    accent: 'amber',
    deepDive: {
      whatItIs: 'ROMIND KIDS is developing a future physical recognition system where children may work toward collectible awards over several years based on positive participation, creativity, consistency, kindness, and contribution.',
      whyKids: 'A real, tangible journey of recognition for the things that actually matter — creating, being kind, contributing to a club, sticking with a craft. Something to look forward to that is not a like count or a follower number.',
      whyParents: 'Planned tiers include Bronze Drop, Silver Drop, Gold Drop, Diamond Drop, and a final rare special Drop currently in development. This is not gambling, not a paid prize draw, not a random chance mechanic, and not a guaranteed reward.',
      ecosystem: 'Recognition is connected to Clubs, Challenges, Times Cards, and long-term Profile growth. It is the long-arc reward for the child\'s creative life on the platform.',
    },
    report: {
      purpose: 'Reward long-term positive participation, not viral popularity.',
      childBenefit: 'A multi-year recognition arc tied to creativity and kindness.',
      parentBenefit: 'No paid lottery, no random chance, no monetary prize draw.',
      safetyNote: 'Eligibility and any parental consent will be communicated transparently before any Drops are issued.',
      connectedTo: ['Clubs & Challenges', 'Times Cards', 'Profiles & Identity'],
    },
  },
  {
    id: 'international',
    title: 'International Friendship & Language Exchange',
    short: 'Safe peer connection across countries through interests, clubs, and challenges.',
    icon: 'Globe',
    color: 'from-cyan-400 to-teal-700',
    accent: 'teal',
    deepDive: {
      whatItIs: 'ROMIND KIDS is designed to support international friendship and natural language exchange through shared interests, clubs, challenges, and safe peer communication.',
      whyKids: 'The goal is not formal lessons. Children connect around drawing, music, stories, games, culture, friendship, and creativity — and language practice happens naturally through meaningful interaction.',
      whyParents: 'Cross-border interaction lives inside the same age-aware safety architecture as the rest of the platform: friend-based communication, RONI guidance, reporting and blocking, and privacy-conscious identity.',
      ecosystem: 'International friendship is enabled by Clubs, Challenges, Messenger, and Profiles. It is a natural consequence of the platform\'s interest-based design.',
    },
    report: {
      purpose: 'Enable natural cross-cultural friendship through shared creative interests.',
      childBenefit: 'Real peer-to-peer cultural exchange, not forced curriculum.',
      parentBenefit: 'Cross-border interaction within the same safety layer as the rest of the app.',
      safetyNote: 'Friend-based communication + RONI guidance + reporting/blocking apply globally.',
      connectedTo: ['Clubs & Challenges', 'Child-Safe Messenger', 'RONI Safety Companion'],
    },
  },
];

// Attach the real ROMIND KIDS render to each feature where one exists.
coreFeatures.forEach((f) => { f.previewImage = FEATURE_RENDERS[f.id] || null; });

export const findFeatureById = (id) => coreFeatures.find((f) => f.id === id);
